import React from "react";
import { useSelector } from "react-redux";
import { useQuery } from "@tanstack/react-query";
import {
  Box, Container, Typography, useMediaQuery, useTheme, Grid, Stack, Skeleton,
} from "@mui/material";

import AdvantagesSection from "./AdvantagesSection";
import HeroSection from "../../components/common/HeroSection";
import { getStudyProgramPage } from "../../services/StudyProgramPageService";
import AboutPage from "../../components/common/AboutPage";
import QuizComponent from "./QuizComponent";
import UniversitySection from "./UniversitySection";

const StudyProgram = () => {
  const { country_id, country_name } = useSelector((state) => state.auth || {});
  
  const { data: programData, isLoading: loading } = useQuery({
    queryKey: ["studyProgramPage", country_id],
    queryFn: async () => {
      if (!country_id) return null;
      const res = await getStudyProgramPage(country_id);
      if (res?.data?.success && res?.data?.data) {
        return res.data.data;
      }
      return null;
    },
    enabled: !!country_id,
  });

  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));

  if (loading) {
    return (
      <Container
        sx={{
          py: { xs: 3, sm: 4, md: 6 },
          px: { xs: 3, sm: 4, md: 6 },
        }}
      >
        <Grid container spacing={2} alignItems="center">
          <Grid size={{ xs: 12, md: 6 }}>
            <Skeleton variant="text" height={60} width="80%" />
            <Skeleton variant="text" height={30} width="90%" />
            <Skeleton variant="text" height={30} width="85%" />
            <Stack direction="row" spacing={2} mt={3}>
              <Skeleton variant="rounded" width={140} height={45} />
              <Skeleton variant="rounded" width={140} height={45} />
            </Stack>
            <Stack spacing={2} mt={3}>
              <Skeleton variant="text" width="70%" />
              <Skeleton variant="text" width="60%" />
              <Skeleton variant="text" width="75%" />
            </Stack>
          </Grid>
          <Grid size={{ xs: 12, md: 6 }}>
            <Skeleton variant="rectangular" height={420} sx={{ borderRadius: 2 }} />
          </Grid>
        </Grid>
      </Container>
    );
  }

  if (!programData)
    return (
      <Box sx={{ textAlign: "center", mt: 10 }}>
        <p>No study program data found for {country_name || "selected country"}.</p>
      </Box>
    );

  const heroTitle = programData.hero_section_title || "";
  const heroSubtitle = programData.hero_section_desc || "";
  const heroImageType = programData.hero_section_image_type || "image";
  const heroImage = programData.hero_section_image || "";

  const advantages =
    programData.advantages?.map((item) => ({
      title: item.key,
      description: item.value,
      url: item.url
    })) || [];

  const aboutSection = {
    desc: programData.about_desc,
    image: programData.about_image,
    imageType: programData.about_image_type,
    advantages_title: programData.advantages_title,
    about_title: programData.about_title,
  };

  const pageData = {
    title: programData.university_docs_title,
    Desc: programData.university_docs_desc,
    shortDesc: programData.university_docs_short_desc,
    documents: programData.university_documents,
    document_list: programData.university_docs_list
  };

  return (
    <>
      <HeroSection
        titleContent={heroTitle}
        subtitleContent={heroSubtitle}
        heroImage={heroImage}
        heroImageType={heroImageType}
      />
      <AdvantagesSection title={aboutSection.advantages_title} countryName={country_name} advantages={advantages} />
      <AboutPage
        title={aboutSection.about_title}
        desc={aboutSection.desc}
        image={aboutSection.image}
        image_type={aboutSection.imageType}
      />
      <UniversitySection pageData={pageData} />
      <QuizComponent />
    </>
  );
};

export default StudyProgram;
